import { useStoryblokApi } from "@storyblok/react";
import { Suspense, use, useEffect, useMemo, useRef } from "react";
import { Link, NavLink, useLocation, useParams, useSearchParams } from "react-router-dom";
import { getEnvironmentVersion } from "../../../utils/getEnvironmentVersion";
import {
  ArticleList,
  OVERVIEW_PATH,
  categoryName,
  categoryPath,
  fetchCategoryNames,
} from "../articleCards";

const POSTS_PER_PAGE = 21;

export default function BlogOverview({ blok }) {
  const storyblokApi = useStoryblokApi();
  const [searchParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1);
  const { category } = useParams();

  const articlesPromise = useMemo(() => {
    return storyblokApi.get("cdn/stories", {
      version: getEnvironmentVersion(),
      content_type: "blogpost",
      sort_by: "content.published_at:desc",
      per_page: POSTS_PER_PAGE,
      page,
      ...(category ? { filter_query: { categories: { in_array: category } } } : {}),
    });
  }, [storyblokApi, page, category]);

  // Category names rarely change, so fetch once per mount.
  const categoryNamesPromise = useMemo(() => fetchCategoryNames(storyblokApi), [storyblokApi]);

  return (
    <section className="blog-index">
      <header className="blog-index-header">
        <h1>
          <span className="blog-index-title">{blok.title}</span>
          {blok.subheading ? <span className="blog-index-subheading">{blok.subheading}</span> : null}
        </h1>
      </header>

      {/* Outside the keyed boundary below so it stays put while posts load. */}
      <Suspense fallback={null}>
        <CategoryFilter getCategoryNames={categoryNamesPromise} />
      </Suspense>

      {/* Keyed by category + page so a change mounts a fresh boundary that
          shows the loader. Otherwise router navigations (transitions) keep the
          old page on screen and never commit, so useMemo re-creates the
          promise forever. */}
      <Suspense
        key={`${category}:${page}`}
        fallback={
          <div className="center">
            <span className="cat-loader"></span>
          </div>
        }
      >
        <ArticlePage
          getArticles={articlesPromise}
          getCategoryNames={categoryNamesPromise}
          page={page}
        />
      </Suspense>
    </section>
  );
}

// Categories in the order they're listed in the blog-categories datasource,
// so editors control the order in Storyblok.
const CategoryFilter = ({ getCategoryNames }) => {
  const categoryNames = use(getCategoryNames);
  const listRef = useRef(null);
  const { pathname } = useLocation();

  // On phones the row scrolls sideways; bring the active pill into view
  // (e.g. "Personal" at the far end). Only the row scrolls, not the page.
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector("a.active");
    if (!active || list.scrollWidth <= list.clientWidth) return;
    const { left, right } = active.getBoundingClientRect();
    const bounds = list.getBoundingClientRect();
    if (left < bounds.left || right > bounds.right) {
      list.scrollLeft += left - bounds.left - (bounds.width - (right - left)) / 2;
    }
  }, [pathname]);

  if (!categoryNames.size) return null;

  return (
    <nav className="blog-category-filter" aria-label="Filter by category">
      <ul ref={listRef}>
        <li>
          {/* `end` so "All" isn't also active on /community/category/... */}
          <NavLink to={OVERVIEW_PATH} end>
            All
          </NavLink>
        </li>
        {[...categoryNames.keys()].map((slug) => (
          <li key={slug}>
            <NavLink to={categoryPath(slug)}>{categoryName(slug, categoryNames)}</NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const ArticlePage = ({ getArticles, getCategoryNames, page }) => {
  const { data, total } = use(getArticles);
  const categoryNames = use(getCategoryNames);
  const pageCount = Math.max(1, Math.ceil(total / POSTS_PER_PAGE));

  return (
    <>
      {data.stories.length ? (
        <ul className="blog-card-grid">
          <ArticleList articles={data.stories} categoryNames={categoryNames} />
        </ul>
      ) : (
        <p className="blog-index-empty">No posts found.</p>
      )}
      <Pagination page={page} pageCount={pageCount} />
    </>
  );
};

// Page 1 links to the bare overview URL so it stays the canonical one.
const pageHref = (page) => ({ search: page > 1 ? `?page=${page}` : "" });

const Pagination = ({ page, pageCount }) => {
  if (pageCount <= 1) return null;

  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);

  return (
    <nav className="blog-pagination" aria-label="Blog pages">
      {page > 1 ? (
        <Link to={pageHref(page - 1)} className="blog-pagination-step">
          Previous
        </Link>
      ) : null}
      {pages.map((p) =>
        p === page ? (
          <span key={p} className="blog-pagination-current" aria-current="page">
            {p}
          </span>
        ) : (
          <Link key={p} to={pageHref(p)}>
            {p}
          </Link>
        ),
      )}
      {page < pageCount ? (
        <Link to={pageHref(page + 1)} className="blog-pagination-step">
          Next
        </Link>
      ) : null}
    </nav>
  );
};
